import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban, CalendarX2, Layers, Lock, MapPin, Plus, Sparkles, Sprout, Users, Zap,
  type LucideIcon,
} from 'lucide-react';
import { api, ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { hasRole } from '../../auth/roles';
import { fmtFormat } from '../../lib/formats';
import { useReturnFromCheckout } from '../../lib/useReturnFromCheckout';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import {
  applyBlockBooking, createBooking, createCheckoutSession, fetchDay,
  fetchMyTeams, fetchTeamCoaches, fetchVenues, previewBlockBooking, simulatePayment,
  type BlockBookingBody, type BlockPreview, type DayAvailability, type GridSlot,
  type MyTeam, type PitchAvailability, type SlotOccupancy,
} from './bookingApi';
import { BlockPreviewSheet } from './BlockPreviewSheet';
import { SlotSheet, type SlotSelection } from './SlotSheet';
import './booking.css';

const SURFACES: Record<string, { label: string; icon: LucideIcon }> = {
  ThreeG: { label: '3G', icon: Layers },
  Grass: { label: 'Grass', icon: Sprout },
};

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Today plus up to four full weeks ahead as selectable chips, capped by the club's booking
 * horizon (mirrored via /api/public/features; the server enforces the real bound).
 */
const RAIL_DAYS_AHEAD = 28;

function upcomingDates(daysAhead: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = 0; i <= daysAhead; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push(toDateInput(d));
  }
  return out;
}

/** Smallest-sided pitches first on the booking page: 5v5 → 7v7 → 9v9 → 11v11. */
const FORMAT_ORDER = ['ThreeVThree', 'FiveVFive', 'SevenVSeven', 'NineVNine', 'ElevenVEleven'];
const formatRank = (format: string) => {
  const i = FORMAT_ORDER.indexOf(format);
  return i === -1 ? FORMAT_ORDER.length : i;
};

/** 3G sections before grass within each venue; unknown surfaces trail. */
const SURFACE_ORDER = ['ThreeG', 'Grass'];
const surfaceRank = (surface: string) => {
  const i = SURFACE_ORDER.indexOf(surface);
  return i === -1 ? SURFACE_ORDER.length : i;
};

const countFree = (pitches: PitchAvailability[]) =>
  pitches.reduce((n, p) => n + p.slots.filter((s) => s.state === 'Free').length, 0);

/**
 * The most weeks an admin may block book — mirror of BlockBookingEndpoints.AdminMaxBlockWeeks
 * (the validators' 12-week hard bound). The club's Booking_MaxBlockWeeks cap stays the coach
 * limit; whole-term series belong to the admin scheduler.
 */
const ADMIN_MAX_BLOCK_WEEKS = 12;

/**
 * How far back an admin may jump the date picker — mirror of the server's
 * ScheduleAdminEndpoints.MaxBackdateDays bound (recording that a slot was already used is the
 * use case; arbitrary historical records are not).
 */
const ADMIN_MAX_BACKDATE_DAYS = 90;

interface PublicFeatures {
  matchRequests: boolean;
  maxBlockWeeks: number;
  aiScheduler: boolean;
  bookingMinMinutes: number;
  bookingMaxMinutes: number;
  bookingHorizonDays: number;
}

export function BookPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  // Admins get the same page with two extras in the slot sheet: a "Booking for" picker over
  // every active team (the server returns them all from /api/teams/mine for admins; asked per
  // booking, in the sheet, because clubs reuse team names across age groups) and free of charge.
  const isAdmin = !!user && hasRole(user.role, 'Admin');
  const queryClient = useQueryClient();
  const [teamId, setTeamId] = useState<number | null>(null);
  const [selection, setSelection] = useState<SlotSelection | null>(null);
  const [blockDraft, setBlockDraft] = useState<{
    pitchName: string;
    body: BlockBookingBody;
    preview: BlockPreview;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  // Both checkout paths close their sheet the moment the booking is created, so from then until
  // the browser has actually left for Stripe there is no button left to spin. This overlay covers
  // the whole checkout leg — session creation as well as the navigation itself — and blocks the
  // grid so a second slot cannot be started mid-handoff. It goes up in the same render that closes
  // the sheet, and is reset when checkout fails — or when the coach comes back from Stripe
  // without paying (browser Back / the PWA sheet's Done button), which never reloads this page.
  const [redirecting, setRedirecting] = useState(false);
  useReturnFromCheckout(redirecting, () => {
    setRedirecting(false);
    toast('Checkout closed — your slot is still held, pay from My bookings before the hold expires', 'info');
    void queryClient.invalidateQueries({ queryKey: ['availability'] });
  });

  const teams = useQuery({ queryKey: ['teams', 'mine'], queryFn: fetchMyTeams });
  // Payment-request recipients for on-behalf bookings — admin-only endpoint, admin-only fetch.
  const teamCoaches = useQuery({
    queryKey: ['team-coaches'],
    queryFn: fetchTeamCoaches,
    enabled: isAdmin,
    staleTime: 60_000,
  });
  // Cosmetic mirrors of server-enforced policy — hide the Match toggle / repeat chips when off.
  const features = useQuery({
    queryKey: ['public-features'],
    queryFn: () => api<PublicFeatures>('/api/public/features'),
    staleTime: 60_000,
  });
  const railDays = Math.min(RAIL_DAYS_AHEAD, features.data?.bookingHorizonDays ?? RAIL_DAYS_AHEAD);
  const dates = useMemo(() => upcomingDates(railDays), [railDays]);
  const [date, setDate] = useState(dates[0]);

  // Every bookable venue gets its own stacked section — one availability fetch per venue.
  const venues = useQuery({ queryKey: ['venues'], queryFn: fetchVenues });
  const days = useQueries({
    queries: (venues.data ?? []).map((v) => ({
      // 'Training' is in the key so the cache can never serve the wrong perspective if the
      // fetch ever becomes type-dynamic (buffers are match-only; see fetchDay).
      queryKey: ['availability', v.id, date, 'Training'],
      queryFn: () => fetchDay(v.id, date, 'Training'),
      refetchInterval: 30_000, // keep the grid honest while the coach dithers
    })),
  });

  // Admins have no page-level team: they say who each booking is for in the slot sheet, so
  // nothing here should highlight "their" format or silently default to the first team.
  const selectedTeam = isAdmin
    ? null
    : teams.data?.find((t) => t.id === (teamId ?? teams.data?.[0]?.id)) ?? teams.data?.[0] ?? null;

  const totalFree = days.reduce(
    (n, q) => n + (q.data && !q.data.closed ? countFree(q.data.pitches) : 0), 0);
  const dateLabel = new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  async function book(
    sel: SlotSelection,
    sheetTeamId: number | null,
    payerUserId: number | null,
    type: 'Training' | 'Match',
    matchType: string | null,
    notes: string,
    weeks: number,
    focReason: string | null,
  ) {
    // Admins name the team in the sheet; coaches picked theirs on the page.
    const team = sheetTeamId !== null
      ? teams.data?.find((t) => t.id === sheetTeamId) ?? null
      : selectedTeam;
    if (!team) return;

    if (type === 'Training' && weeks > 1) {
      await openBlockPreview(sel, team, payerUserId, notes, weeks, focReason);
      return;
    }

    setBusy(true);
    try {
      const created = await createBooking({
        pitchId: sel.pitchId,
        teamId: team.id,
        startUtc: sel.startUtc,
        endUtc: sel.endUtc,
        type,
        matchType,
        notes: notes || null,
        isFoc: focReason !== null,
        focReason,
        payerUserId,
      });
      setSelection(null);
      // Fire-and-forget: the refetch repaints the grid behind the overlay/toast — checkout must
      // never queue behind it.
      void queryClient.invalidateQueries({ queryKey: ['availability'] });

      if (created.status === 'PendingApproval') {
        toast('Match request sent — an admin will review it', 'success');
        return;
      }

      // Admin bookings approve on the spot: on-behalf ones raise the debt with the coach, and
      // an admin's own non-FOC match is theirs to pay from My bookings (no checkout leg here).
      if (created.status === 'ApprovedPendingPayment') {
        toast(
          payerUserId !== null
            ? 'Booked — payment request sent to the coach'
            : 'Booked — pay for it from My bookings',
          'success',
        );
        return;
      }

      // A £0 slot (free-of-charge pricing rule) confirms server-side with nothing to pay.
      if (created.status === 'Confirmed') {
        toast('Booking confirmed — this slot is free of charge', 'success');
        return;
      }

      // Training: straight to payment. The overlay appears in the same render that closed the
      // sheet, so the coach gets feedback before the checkout-session round trip, not after it.
      setRedirecting(true);
      try {
        const session = await createCheckoutSession(created.bookingId);
        window.location.href = session.url;
      } catch (err) {
        setRedirecting(false);
        if (err instanceof ApiError && err.code === 'payments-not-configured') {
          try {
            await simulatePayment(created.bookingId);
            toast('Payment simulated (dev mode) — booking confirmed', 'success');
            await queryClient.invalidateQueries({ queryKey: ['availability'] });
          } catch {
            // Simulator absent (production without Stripe configured) — be honest.
            toast('Online payment is not set up yet — contact the club to complete this booking', 'error');
          }
        } else {
          toast('Booking held — pay from My bookings before the hold expires', 'info');
        }
      }
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not create the booking', 'error');
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
    } finally {
      setBusy(false);
    }
  }

  /** Step 1 of a block booking: server evaluates every week; nothing is created yet. */
  async function openBlockPreview(
    sel: SlotSelection,
    team: MyTeam,
    payerUserId: number | null,
    notes: string,
    weeks: number,
    focReason: string | null,
  ) {
    setBusy(true);
    try {
      const body: BlockBookingBody = {
        pitchId: sel.pitchId,
        teamId: team.id,
        startUtc: sel.startUtc,
        endUtc: sel.endUtc,
        weeks,
        notes: notes || null,
        isFoc: focReason !== null,
        focReason,
        payerUserId,
      };
      const preview = await previewBlockBooking(body);
      setSelection(null);
      setBlockDraft({ pitchName: sel.pitchName, body, preview });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not preview the block booking', 'error');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Step 2: create the held weeks, then pay for the FIRST one. Weeks are billed individually, so
   * this uses the ordinary single-booking checkout rather than the legacy series session (which
   * exists only to settle blocks booked before per-week billing). Until this payment lands the
   * whole block shares one hold and lapses together; settling it converts the rest to money owed.
   */
  async function confirmBlock() {
    if (!blockDraft) return;
    setBusy(true);
    try {
      const applied = await applyBlockBooking({
        ...blockDraft.body,
        previewFingerprint: blockDraft.preview.previewFingerprint,
      });
      setBlockDraft(null);
      // Fire-and-forget for the same reason as book(): a block invalidates every venue's day
      // query, and awaiting those refetches held the Stripe handoff hostage to the grid repaint —
      // the worst of the "nothing happens after I click pay" reports.
      void queryClient.invalidateQueries({ queryKey: ['availability'] });

      // On-behalf block: every week is already owed by the coach — payment requests, no checkout.
      if (blockDraft.body.payerUserId) {
        toast(
          `${applied.bookingIds.length} week${applied.bookingIds.length === 1 ? '' : 's'} booked — payment request sent to the coach`,
          'success',
        );
        return;
      }

      // Nothing due (all weeks free of charge) — the whole block confirmed at apply time.
      if (applied.dueNowPence === 0) {
        toast(
          `${applied.bookingIds.length} week${applied.bookingIds.length === 1 ? '' : 's'} booked — free of charge`,
          'success',
        );
        return;
      }

      // Pay the first PAYABLE week — £0 weeks (free-of-charge pricing) confirm at apply time
      // and are never billed, so the first row may already be confirmed. bookingIds and
      // createdWeeks are both ordered by start.
      const firstPayableIndex = applied.createdWeeks.findIndex((w) => (w.pricePence ?? 0) > 0);
      const firstBookingId = applied.bookingIds[firstPayableIndex === -1 ? 0 : firstPayableIndex];
      setRedirecting(true);
      try {
        const session = await createCheckoutSession(firstBookingId);
        window.location.href = session.url;
      } catch (err) {
        setRedirecting(false);
        if (err instanceof ApiError && err.code === 'payments-not-configured') {
          try {
            await simulatePayment(firstBookingId);
            toast(
              `Payment simulated (dev mode) — first week confirmed, ${applied.bookingIds.length - 1} to pay later`,
              'success',
            );
            await queryClient.invalidateQueries({ queryKey: ['availability'] });
          } catch {
            // Simulator absent (production without Stripe configured) — be honest.
            toast('Online payment is not set up yet — contact the club to complete this booking', 'error');
          }
        } else {
          toast('Weeks held — pay for the first one from My bookings before the hold expires', 'info');
        }
      }
    } catch (err) {
      // Availability moved between preview and apply: refresh the preview instead of failing.
      if (err instanceof ApiError && err.status === 409 && err.problem?.reason === 'fingerprint-mismatch') {
        try {
          const preview = await previewBlockBooking(blockDraft.body);
          setBlockDraft({ ...blockDraft, preview });
          toast('Availability changed — review the updated weeks', 'info');
          return;
        } catch {
          /* fall through to the generic error */
        }
      }

      toast(err instanceof ApiError ? err.message : 'Could not create the block booking', 'error');
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
    } finally {
      setBusy(false);
    }
  }

  if (teams.isLoading) return <Spinner center />;

  if (!teams.data || teams.data.length === 0) {
    return (
      <div className="book-empty">
        <Users size={36} aria-hidden />
        <h2>No team yet</h2>
        {isAdmin ? (
          <p>There are no active teams to book for — create one under Admin → Teams first.</p>
        ) : (
          <p>
            You need to be assigned to a team before booking — ask a club admin.
            Once you're linked to your team(s), the booking grid appears here.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="book-page">
      <header className="book-header">
        <div>
          <h1 className="book-title">Book a pitch</h1>
          <p className="book-subtitle">
            <MapPin size={14} aria-hidden />
            <span>{dateLabel}</span>
          </p>
        </div>
        {days.some((q) => q.data && !q.data.closed) ? (
          <span className="book-free-pill">
            <Sparkles size={14} aria-hidden />
            {totalFree} free slot{totalFree === 1 ? '' : 's'}
          </span>
        ) : null}
      </header>

      <div className="book-toolbar">
        <div className="date-rail" role="tablist" aria-label="Choose a date">
          {dates.map((d, i) => {
            const dt = new Date(`${d}T00:00:00`);
            return (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={d === date}
                className={`date-chip ${d === date ? 'date-chip-active' : ''}`}
                onClick={() => setDate(d)}
              >
                <span className="date-chip-dow">
                  {i === 0 ? 'Today' : dt.toLocaleDateString('en-GB', { weekday: 'short' })}
                </span>
                <span className="date-chip-day">{dt.getDate()}</span>
                <span className="date-chip-month">{dt.toLocaleDateString('en-GB', { month: 'short' })}</span>
              </button>
            );
          })}
        </div>
        {isAdmin ? (
          // Admins can jump anywhere from 90 days back (recording a slot that was already
          // used — the team spotted on the 3G yesterday) to the horizon the rail shows.
          <div className="book-team-row">
            <label className="team-select-label" htmlFor="book-jump-date">Jump to date</label>
            <input
              id="book-jump-date"
              type="date"
              className="ui-select"
              value={date}
              min={toDateInput(new Date(Date.now() - ADMIN_MAX_BACKDATE_DAYS * 86_400_000))}
              max={dates[dates.length - 1]}
              onChange={(e) => {
                if (e.target.value) setDate(e.target.value);
              }}
            />
          </div>
        ) : null}
        {!isAdmin ? (
          <div className="book-team-row">
            <label className="team-select-label" htmlFor="book-team">Booking for</label>
            <select
              id="book-team"
              className="ui-select"
              value={selectedTeam?.id ?? ''}
              onChange={(e) => setTeamId(Number(e.target.value))}
            >
              {teams.data.map((t) => (
                <option key={t.id} value={t.id}>
                  {/* Age group first — clubs reuse team names across age groups. */}
                  {t.ageGroup} {t.name} ({fmtFormat(t.defaultFormat)})
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {date < dates[0] ? (
        <p className="book-past-note" role="note">
          Past date — you&apos;re recording a slot that was already used. It must be billed to a
          coach or booked free of charge.
        </p>
      ) : null}

      {venues.isLoading ? (
        <Spinner center />
      ) : !venues.data || venues.data.length === 0 ? (
        <div className="book-empty">
          <CalendarX2 size={36} aria-hidden />
          <h2>Unavailable</h2>
          <p>Could not load the venues — try refreshing.</p>
        </div>
      ) : (
        venues.data.map((venue, i) => (
          <VenueBlock
            key={venue.id}
            name={venue.name}
            day={days[i]?.data}
            loading={days[i]?.isLoading ?? true}
            selectedTeam={selectedTeam}
            onPick={setSelection}
          />
        ))
      )}

      {selection ? (
        <SlotSheet
          selection={selection}
          minMinutes={features.data?.bookingMinMinutes ?? 60}
          maxMinutes={features.data?.bookingMaxMinutes ?? 180}
          matchRequestsEnabled={features.data?.matchRequests ?? true}
          maxBlockWeeks={
            // Admins can go past the club cap (block booking must still be ON — a cap of 1
            // means the feature is off for everyone).
            isAdmin && (features.data?.maxBlockWeeks ?? 1) >= 2
              ? ADMIN_MAX_BLOCK_WEEKS
              : (features.data?.maxBlockWeeks ?? 1)
          }
          canFoc={isAdmin}
          teams={isAdmin ? teams.data : undefined}
          coachesByTeam={isAdmin ? teamCoaches.data : undefined}
          busy={busy || redirecting}
          onConfirm={book}
          onClose={() => setSelection(null)}
        />
      ) : null}

      {blockDraft ? (
        <BlockPreviewSheet
          pitchName={blockDraft.pitchName}
          preview={blockDraft.preview}
          foc={blockDraft.body.isFoc ?? false}
          paymentRequest={blockDraft.body.payerUserId != null}
          busy={busy || redirecting}
          onConfirm={() => void confirmBlock()}
          onBack={() => setBlockDraft(null)}
        />
      ) : null}

      {redirecting ? (
        <div className="ui-dialog-backdrop" role="alert" aria-live="assertive">
          <div className="ui-dialog bk-redirect">
            <Spinner />
            <p className="bk-redirect-text">Opening secure payment…</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One venue's stacked section: name header with its own free count, then the surface groups.
 * Pitches sort smallest format first (5v5 → 7v7 → 9v9 → 11v11) — the old team's-format-first
 * sort was replaced by this; the team's own format keeps its highlight badge instead.
 */
function VenueBlock({
  name,
  day,
  loading,
  selectedTeam,
  onPick,
}: {
  name: string;
  day: DayAvailability | undefined;
  loading: boolean;
  selectedTeam: MyTeam | null;
  onPick: (selection: SlotSelection) => void;
}) {
  const surfaceGroups = useMemo(() => {
    if (!day) return [];
    const sorted = [...day.pitches].sort(
      (a, b) => formatRank(a.format) - formatRank(b.format) || a.name.localeCompare(b.name),
    );
    const groups = new Map<string, PitchAvailability[]>();
    for (const p of sorted) {
      const list = groups.get(p.surface);
      if (list) list.push(p);
      else groups.set(p.surface, [p]);
    }
    // 3G before grass — the section order is fixed, not an accident of pitch sorting.
    return [...groups.entries()].sort((a, b) => surfaceRank(a[0]) - surfaceRank(b[0]));
  }, [day]);

  const free = day && !day.closed ? countFree(day.pitches) : 0;

  return (
    <section className="venue-block" aria-label={name}>
      <header className="venue-block-header">
        <MapPin size={16} aria-hidden />
        <h2 className="venue-block-title">{name}</h2>
        <span className="surface-rule" aria-hidden />
        {day && !day.closed ? <span className="surface-free">{free} free</span> : null}
      </header>
      {loading ? (
        <Spinner center />
      ) : !day ? (
        <p className="venue-block-note">Could not load availability for this venue — try refreshing.</p>
      ) : day.closed ? (
        <p className="venue-block-note">
          <CalendarX2 size={15} aria-hidden />
          {day.closureReason ?? 'Closed on this date.'}
        </p>
      ) : (
        surfaceGroups.map(([surface, pitches]) => {
          // Object.hasOwn guards prototype keys — surface is server-validated, this is belt and braces.
          const meta = Object.hasOwn(SURFACES, surface) ? SURFACES[surface] : { label: surface, icon: MapPin };
          const SurfaceIcon = meta.icon;
          return (
            <section key={surface} className="surface-section">
              <header className="surface-header">
                <SurfaceIcon size={15} aria-hidden />
                <h3 className="surface-title">{meta.label} pitches</h3>
                <span className="surface-rule" aria-hidden />
                <span className="surface-free">{countFree(pitches)} free</span>
              </header>
              <div className="pitch-columns">
                {pitches.map((pitch) => (
                  <PitchCard
                    key={pitch.pitchId}
                    pitch={pitch}
                    highlight={pitch.format === selectedTeam?.defaultFormat}
                    onPick={(slot) =>
                      onPick({
                        pitchId: pitch.pitchId,
                        pitchName: pitch.name,
                        format: pitch.format,
                        startUtc: slot.startUtc,
                        endUtc: slot.endUtc,
                      })
                    }
                  />
                ))}
              </div>
            </section>
          );
        })
      )}
    </section>
  );
}

function PitchCard({
  pitch,
  highlight,
  onPick,
}: {
  pitch: PitchAvailability;
  highlight: boolean;
  onPick: (slot: GridSlot) => void;
}) {
  return (
    <section
      className={`pitch-card ${highlight ? 'pitch-card-highlight' : ''} ${pitch.isClosed ? 'pitch-card-closed' : ''}`}
    >
      <header className="pitch-card-header">
        <div className="pitch-card-title">
          <span className="pitch-name">{pitch.name}</span>
          {pitch.isFloodlit ? (
            <span className="pitch-floodlit" title="Floodlit">
              <Zap size={13} aria-label="Floodlit" />
            </span>
          ) : null}
        </div>
        <div className="pitch-card-meta">
          <span className="pitch-format-badge">{fmtFormat(pitch.format)}</span>
          {highlight ? <span className="pitch-your-format">Your format</span> : null}
        </div>
      </header>
      {/*
        A closed pitch replaces its slot list entirely. Every slot is blocked anyway, but a column
        of "Booked" would be a lie — nobody has it, the club has shut it.
      */}
      {pitch.isClosed ? (
        <div className="pitch-closed-note" role="status">
          <Ban size={14} aria-hidden />
          <span>
            <strong>Closed</strong>
            {pitch.closureReason ? ` — ${pitch.closureReason}` : ''}
          </span>
        </div>
      ) : (
      <div className="pitch-slots">
        {pitch.slots.map((slot) => {
          const label = new Date(slot.startUtc).toLocaleTimeString('en-GB', {
            hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
          });
          if (slot.state === 'Free') {
            return (
              <button
                key={slot.startUtc}
                type="button"
                className="slot slot-free"
                onClick={() => onPick(slot)}
              >
                <span className="slot-time">{label}</span>
                <Plus size={15} className="slot-plus" aria-hidden />
              </button>
            );
          }
          if (slot.state === 'BlockedBuffer') {
            return (
              <div key={slot.startUtc} className="slot slot-buffer" aria-label="Changeover time">
                changeover
              </div>
            );
          }
          return <BookedSlot key={slot.startUtc} occupancy={occupancyAt(pitch, slot)} />;
        })}
      </div>
      )}
    </section>
  );
}

/**
 * The published occupant covering a slot, if any. A slot can be hard-blocked with no occupancy on
 * THIS pitch — a booking on a physically overlapping pitch, or an unpublished club allocation —
 * and then it stays an anonymous "Booked" cell.
 */
function occupancyAt(pitch: PitchAvailability, slot: GridSlot): SlotOccupancy | null {
  const t = Date.parse(slot.startUtc);
  return pitch.occupancies.find((o) => Date.parse(o.startUtc) <= t && t < Date.parse(o.endUtc)) ?? null;
}

/**
 * A booked cell. When the occupant is published it names the team, and hover/focus/tap raises a
 * tooltip listing the team's coaches — so a coach who wants the slot knows who to talk to about
 * moving things around. Portalled to document.body with fixed positioning (the scheduler lesson:
 * anchored inside the card it would be clipped by the column). Tap shows rather than toggles —
 * touch fires mouseenter before click, so a toggle would flash the tip and immediately hide it;
 * tapping anywhere else blurs the button and dismisses.
 */
function BookedSlot({ occupancy }: { occupancy: SlotOccupancy | null }) {
  const ref = useRef<HTMLButtonElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; below: boolean } | null>(null);

  if (!occupancy) {
    return (
      <div className="slot slot-booked" aria-label="Booked">
        <Lock size={11} aria-hidden />
        <span>Booked</span>
      </div>
    );
  }

  const team = `${occupancy.teamAgeGroup} ${occupancy.teamName}`;
  const coaches = occupancy.coachNames;
  const coachLine = coaches.length
    ? `Coach${coaches.length === 1 ? '' : 'es'}: ${coaches.join(', ')}`
    : 'No coaches listed';
  // The team is on a physically overlapping pitch — this cell is collateral, and says so.
  const whereLine = occupancy.onPitchName ? `Playing on ${occupancy.onPitchName} — overlaps this pitch` : null;

  function show() {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const below = r.top < 120; // not enough viewport room above — flip under the cell
    const x = Math.min(Math.max(r.left + r.width / 2, 120), window.innerWidth - 120);
    setTip({ x, y: below ? r.bottom : r.top, below });
  }

  return (
    <button
      ref={ref}
      type="button"
      className={`slot slot-booked slot-booked-named ${whereLine ? 'slot-booked-overlap' : ''}`}
      aria-label={`Booked by ${team}${whereLine ? ` — ${whereLine}` : ''} — ${coachLine}`}
      onMouseEnter={show}
      onMouseLeave={() => setTip(null)}
      onFocus={show}
      onBlur={() => setTip(null)}
      onClick={show}
    >
      <Lock size={11} aria-hidden />
      <span className="slot-booked-team">{team}</span>
      {tip
        ? createPortal(
            <div
              className={`book-tip ${tip.below ? 'tip-below' : ''}`}
              style={{ left: tip.x, top: tip.y }}
              role="tooltip"
            >
              <span className="book-tip-title">{team}</span>
              {whereLine ? <span className="book-tip-who">{whereLine}</span> : null}
              <span className="book-tip-who">{coachLine}</span>
            </div>,
            document.body,
          )
        : null}
    </button>
  );
}
